import React, { useId, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Area, AreaChart, ResponsiveContainer, Tooltip, YAxis } from 'recharts';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const formatDayLabel = (value) => {
  if (!value) return '—';
  try {
    return format(parseISO(value), "dd 'de' MMM", { locale: ptBR });
  } catch {
    return String(value);
  }
};

const SparklineTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  return (
    <div
      style={{
        background: 'var(--color-bg-primary)',
        border: '1px solid var(--color-border-secondary)',
        borderRadius: 6,
        padding: '0.3rem 0.5rem',
        fontSize: '0.75rem',
        lineHeight: 1.35,
        boxShadow: 'var(--shadow-md)',
        whiteSpace: 'nowrap',
      }}
    >
      <div style={{ color: 'var(--color-text-secondary)' }}>{point?.label}</div>
      <div style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>
        {point?.count} {point?.count === 1 ? 'ticket' : 'tickets'}
      </div>
    </div>
  );
};

const OpenCountSparkline = ({ dailyCounts = [], color = 'var(--color-text-primary)', height = 44 }) => {
  const gradientId = `spark-${useId().replace(/:/g, '')}`;
  const chartData = useMemo(
    () =>
      (Array.isArray(dailyCounts) ? dailyCounts : []).map((row) => ({
        date: row?.date,
        label: formatDayLabel(row?.date),
        count: Number(row?.count) || 0,
      })),
    [dailyCounts]
  );

  if (chartData.length === 0) return null;

  const maxCount = Math.max(...chartData.map((row) => row.count), 1);

  return (
    <div style={{ width: '100%', height }} onClick={(e) => e.stopPropagation()}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 4, right: 2, left: 2, bottom: 2 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.22} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <YAxis hide domain={[0, maxCount * 1.15]} allowDecimals={false} />
          <Tooltip
            content={<SparklineTooltip />}
            cursor={{ stroke: 'var(--color-border-secondary)', strokeWidth: 1 }}
            isAnimationActive={false}
          />
          <Area
            type="monotone"
            dataKey="count"
            stroke={color}
            strokeWidth={1.75}
            fill={`url(#${gradientId})`}
            dot={false}
            activeDot={{ r: 3, strokeWidth: 2, stroke: 'var(--color-bg-primary)', fill: color }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

OpenCountSparkline.propTypes = {
  dailyCounts: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
      count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
  color: PropTypes.string,
  height: PropTypes.number,
};

export default OpenCountSparkline;
